import {
  ViewerApi,
  CoursewareStructure,
  Deployment,
  Pathway,
  Screen,
} from "@bronte/viewer-api/src/main";
import {
  CoursewareElementTypes,
  CoursewareLessonTypes,
  PathwayTypes,
} from "@bronte/viewer/src/main";
import { getTitle } from "../../shared/utils";
import { Toc, TOCStitchedLessonMapItem } from "./types";
import TocHtmlBuilder from "./TocHtmlBuilder";

let tocHtmlBuilder: TocHtmlBuilder;

export let firstRenderId: string;
// TODO: building a partial toc map for only stitched lesson. This should be changed to a full toc map inline with offline toc map BRNT-29613
export const tocStitchedLessonMap: Map<string, TOCStitchedLessonMapItem> = new Map([]);

export const processEdge = (edge: { node: Screen }, result: Screen[]): void => {
  const currentNode = edge.node;
  const elementId = currentNode.elementId;
  const elementTitle = getTitle(currentNode.configurationFields);
  const isStandardLesson =
    currentNode.plugin?.manifest.name === CoursewareLessonTypes.LESSON_TYPE_STANDARD;

  if (isStandardLesson && currentNode.pathways) {
    const standardLessonFirstScreen =
      currentNode.pathways?.[0]?.walkables?.edges?.[0]?.node?.elementId;
    tocHtmlBuilder.addStandardLessonListItem(elementId, elementTitle, standardLessonFirstScreen);
  } else {
    tocHtmlBuilder.addDefaultListItem(elementId, elementTitle);
  }

  if (currentNode.elementType === CoursewareElementTypes.ELEMENT_TYPE_ACTIVITY) {
    tocHtmlBuilder.startList(elementId);
    processActivityNode(currentNode, result);
    tocHtmlBuilder.endList();
  } else {
    setFirstRenderIdIfNotSet(currentNode);
    result.push(currentNode);
    tocHtmlBuilder.closeListItem();
  }
};

export const appendPathways = (pathways: Pathway[]): Screen[] => {
  const result: Screen[] = [];

  pathways.forEach((pathway) => {
    if (pathway.type !== PathwayTypes.PATHWAY_TYPE_GRAPH) {
      tocHtmlBuilder.startList(pathway.id);
      pathway.walkables.edges.forEach((edge) => processEdge(edge, result));
      tocHtmlBuilder.endList();
    }
  });

  return result;
};

export const processActivityNode = (node: Screen, result: Screen[]): void => {
  const isStitchedLesson =
    node.plugin?.manifest.name === CoursewareLessonTypes.LESSON_TYPE_STITCHED;

  const isLabLesson = node.plugin?.manifest.name === CoursewareLessonTypes.LESSON_TYPE_LAB;

  if (isStitchedLesson) {
    processStitchedLesson(node, result);
  } else if (isLabLesson) {
    processLabLesson(node, result);
  } else {
    result.push(...appendPathways(node.pathways));
  }
};

export const processLabLesson = (node: Screen, result: Screen[]): void => {
  setFirstRenderIdIfNotSet(node);
  result.push(node);
};

export const processStitchedLesson = (node: Screen, result: Screen[]): void => {
  setFirstRenderIdIfNotSet(node);
  result.push(node);

  node.pathways?.forEach((childPathway) => {
    tocHtmlBuilder.startList(childPathway.id);
    childPathway.walkables.edges.forEach((childEdge) => {
      const childNode = childEdge.node;
      const childElementId = childNode.elementId; // the element Id of the screens within a stitched lesson
      const parentElementId = node.elementId; // the elementId of the stitched lesson which contains the screens
      const childElementTitle = getTitle(childNode.configurationFields);
      // ------ toc stitched lesson map create BRNT-29613
      tocStitchedLessonMap.set(childElementId, {
        isScreen: true,
        elementToRoute: parentElementId,
      });
      // ----------------
      tocHtmlBuilder.addStitchedLessonListItem(parentElementId, childElementTitle, childElementId);
      tocHtmlBuilder.closeListItem();
    });
    tocHtmlBuilder.endList();
  });
};

export const setFirstRenderIdIfNotSet = (node: Screen): void => {
  if (!firstRenderId) {
    firstRenderId = node.elementId;
  }
};

export const getTocForWeb = async (viewerApi: ViewerApi): Promise<() => Toc> => {
  tocHtmlBuilder = new TocHtmlBuilder();
  const toc: CoursewareStructure = await viewerApi.getCoursewareStructure();
  let screens: Screen[] = [];

  return () => {
    toc?.learn?.cohort?.deployment.forEach((deployment: Deployment) => {
      screens = [...screens, ...appendPathways(deployment.activity.pathways)];
    });

    return {
      tocHtmlNode: tocHtmlBuilder.getStructure(),
      firstRenderId,
      screens,
      tocStitchedLessonMap,
    };
  };
};
